package com.macrofocus.docking.js

import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.docking.*
import com.macrofocus.docking.js.splitter.CPThreeComponentsSplitter
import com.macrofocus.docking.splitter.ThreeComponentsSplitter
import js.objects.unsafeJso
import org.mkui.component.CPComponent
import org.mkui.component.ReactComponent
import org.mkui.react.useProperty
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import web.cssom.*

/**
 * React counterpart of the Swing `SwingDockingPanel`.
 *
 * The docking bars surround the content in a 3x3 grid: the west and east bars are as wide as their tabs and the
 * north and south bars as high as theirs, the corners being empty. The content is made of two nested
 * [CPThreeComponentsSplitter]s: when [vertical] the primary splitter stacks north / (west | inner | east) / south,
 * otherwise it puts west / (north | inner | south) / east side by side.
 */
class JsDockingPanel(private var vertical: Boolean = true) : DockingPanel<CPComponent> {
    val center : MutableProperty<CPComponent?> = SimpleProperty<CPComponent?>(null)

    val westContainerPanel: DockingContainer = DockingContainer()
    val eastContainerPanel: DockingContainer = DockingContainer()
    val southContainerPanel: DockingContainer = DockingContainer()
    val northContainerPanel: DockingContainer = DockingContainer()

    private val primarySplitter: CPThreeComponentsSplitter = CPThreeComponentsSplitter(vertical)
    private val secondarySplitter: CPThreeComponentsSplitter = CPThreeComponentsSplitter(!vertical)
    override val westDockingBar: DockingBar<CPComponent> = JsDockingBar(westContainerPanel, DockingAnchor.LEFT)
    override val eastDockingBar: DockingBar<CPComponent> = JsDockingBar(eastContainerPanel, DockingAnchor.RIGHT)
    override val southDockingBar: DockingBar<CPComponent> = JsDockingBar(southContainerPanel, DockingAnchor.BOTTOM)
    override val northDockingBar: DockingBar<CPComponent> = JsDockingBar(northContainerPanel, DockingAnchor.TOP)

    override val nativeComponent: CPComponent = ReactComponent(FC<Props> {
        val (getCenter, _) = useProperty(center)

        div {
            className = ClassName("DockingPanel")
            style = unsafeJso {
                display = Display.grid
                gridTemplateColumns = array(Length.minContent, 1.fr, Length.minContent)
                gridTemplateRows = array(Length.minContent, 1.fr, Length.minContent)
                width = 100.pct
                height = 100.pct
                minWidth = 0.px
                minHeight = 0.px
                position = Position.relative
                overflow = Overflow.hidden
                boxSizing = BoxSizing.borderBox
                backgroundColor = PanelBackground
                pointerEvents = None.none
            }

            JsDockingCorner().nativeComponent()
            northDockingBar.nativeComponent.nativeComponent()
            JsDockingCorner().nativeComponent()

            westDockingBar.nativeComponent.nativeComponent()
            div {
                className = ClassName("DockingPanel-center")
                style = unsafeJso {
                    position = Position.relative
                    minWidth = 0.px
                    minHeight = 0.px
                    overflow = Overflow.hidden
                }
                getCenter?.nativeComponent?.invoke()
            }
            eastDockingBar.nativeComponent.nativeComponent()

            JsDockingCorner().nativeComponent()
            southDockingBar.nativeComponent.nativeComponent()
            JsDockingCorner().nativeComponent()
        }
    })

    override var innerComponent: CPComponent? = null
        set(value) {
            field = value
            updateDockingContainers()
        }

    init {
        westContainerPanel.visibleProperty.addPropertyListener { updateDockingContainers() }
        eastContainerPanel.visibleProperty.addPropertyListener { updateDockingContainers() }
        southContainerPanel.visibleProperty.addPropertyListener { updateDockingContainers() }
        northContainerPanel.visibleProperty.addPropertyListener { updateDockingContainers() }

        updateDockingContainers()
        secondarySplitter.setShowDividerControls(true)
        primarySplitter.setShowDividerControls(true)
        center.value = primarySplitter
    }

    override fun setOrientation(vertical: Boolean) {
        if (this.vertical != vertical) {
            // Keep the size of each docking area: the north/south sizes move from one splitter to the other
            val horizontalFirst = horizontalSplitter.firstSize
            val horizontalLast = horizontalSplitter.lastSize
            val verticalFirst = verticalSplitter.firstSize
            val verticalLast = verticalSplitter.lastSize
            val horizontalRelative = horizontalSplitter.isRelativeResize
            val verticalRelative = verticalSplitter.isRelativeResize

            this.vertical = vertical
            primarySplitter.vertical = vertical
            secondarySplitter.vertical = !vertical

            horizontalSplitter.firstSize = horizontalFirst
            horizontalSplitter.lastSize = horizontalLast
            verticalSplitter.firstSize = verticalFirst
            verticalSplitter.lastSize = verticalLast
            horizontalSplitter.isRelativeResize = horizontalRelative
            verticalSplitter.isRelativeResize = verticalRelative

            updateDockingContainers()
        }
    }

    private val horizontalSplitter: ThreeComponentsSplitter<CPComponent?>
        get() = if (!vertical) primarySplitter else secondarySplitter

    private val verticalSplitter: ThreeComponentsSplitter<CPComponent?>
        get() = if (vertical) primarySplitter else secondarySplitter

    override var horizontalFirstDividerSize: Double
        get() = horizontalSplitter.firstSizeIfVisible
        set(size) {
            horizontalSplitter.firstSize = size
        }

    override var horizontalLastDividerSize: Double
        get() = horizontalSplitter.lastSizeIfVisible
        set(size) {
            horizontalSplitter.lastSize = size
        }

    override var verticalFirstDividerSize: Double
        get() = verticalSplitter.firstSizeIfVisible
        set(size) {
            verticalSplitter.firstSize = size
        }

    override var verticalLastDividerSize: Double
        get() = verticalSplitter.lastSizeIfVisible
        set(size) {
            verticalSplitter.lastSize = size
        }

    override fun setHorizontalRelativeSize(relativeResize: Boolean) {
        horizontalSplitter.isRelativeResize = relativeResize
    }

    override fun setVerticalRelativeResize(relativeResize: Boolean) {
        verticalSplitter.isRelativeResize = relativeResize
    }

    override fun dispose() {
        for (bar in listOf(westDockingBar, eastDockingBar, southDockingBar, northDockingBar)) {
            for (dockable in bar.dockables.orEmpty()) {
                dockable?.dispose()
            }
            bar.dispose()
        }
    }

    override fun attach(dockable: Dockable<CPComponent>?, anchor: AttachAnchor?) {
        when (anchor!!) {
            AttachAnchor.TOP -> northDockingBar.attach(dockable!!)
            AttachAnchor.BOTTOM -> southDockingBar.attach(dockable!!)
            AttachAnchor.LEFT -> westDockingBar.attach(dockable!!)
            AttachAnchor.RIGHT -> eastDockingBar.attach(dockable!!)
            AttachAnchor.CENTER -> innerComponent = dockable!!.component
        }
    }

    fun updateDockingContainers() {
        // Hidden docking areas are removed from the splitters so that they collapse, as in Swing
        val westContainerPanel = if (westContainerPanel.visible) westContainerPanel else null
        val eastContainerPanel = if (eastContainerPanel.visible) eastContainerPanel else null
        val northContainerPanel = if (northContainerPanel.visible) northContainerPanel else null
        val southContainerPanel = if (southContainerPanel.visible) southContainerPanel else null
        if (vertical) {
            secondarySplitter.setComponents(westContainerPanel, innerComponent, eastContainerPanel)
            primarySplitter.setComponents(northContainerPanel, secondarySplitter, southContainerPanel)
        } else {
            secondarySplitter.setComponents(northContainerPanel, innerComponent, southContainerPanel)
            primarySplitter.setComponents(westContainerPanel, secondarySplitter, eastContainerPanel)
        }
    }

    override fun createNakedDockable(component: CPComponent, title: String?): Dockable<CPComponent> {
        return createNakedDockable(component, title, null)
    }

    override fun createNakedDockable(component: CPComponent, title: String?, icon: String?): Dockable<CPComponent> {
        return CPNakedDockable(component, title, iconURL = icon)
    }

    override fun createDecoratedDockable(component: CPComponent, title: String?): Dockable<CPComponent> {
        return createDecoratedDockable(component, title, null as String?)
    }

    override fun createDecoratedDockable(component: CPComponent, shortTitle: String?, title: String?, description: String?): Dockable<CPComponent> {
        return createDecoratedDockable(component, shortTitle, title, description, null)
    }

    fun createDecoratedDockable(component: CPComponent, title: String?, iconUrl: String?): Dockable<CPComponent> {
        return JsDecoratedDockable(component, title, title, null, iconUrl)
    }

    fun createDecoratedDockable(component: CPComponent, shortTitle: String?, title: String?, description: String?, iconUrl: String?): Dockable<CPComponent> {
        return JsDecoratedDockable(component, shortTitle, title, description, iconUrl)
    }

    companion object {
        val PanelBackground = rgb(237, 235, 230)
    }
}
